import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Users, Mic, Star, CheckCircle, Play } from 'lucide-react';
import axios from 'axios';
import NewsletterSignup from '../components/NewsletterSignup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="home-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1764874299006-bf4266427ec9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGV4ZWN1dGl2ZSUyMHNwZWFraW5nJTIwYXQlMjBjb25mZXJlbmNlfGVufDB8fHx8MTc2OTI5ODM4Nnww&ixlib=rb-4.1.0&q=85"
            alt="Business executive speaking"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block px-4 py-2 bg-accent/20 text-accent-foreground rounded-full text-sm font-medium mb-6 tracking-wider" data-testid="hero-badge">
                NOW AVAILABLE ON AMAZON
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="hero-title">
                The Customer First Business
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed" data-testid="hero-description">
                Build a multi-million dollar business through the power of servant first leadership and customer-first principles. Learn how putting your customers at the center of everything drives sustainable growth and lasting success.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://www.amazon.com/Customer-First-Business-Blueprint-Innovation-ebook/dp/B0GJFNY1QD/ref=sr_1_1?crid=GGCBYQQLBBZG&dib=eyJ2IjoiMSJ9.6vYa2tjD0i-VCnfODAfs3XdC9GcgwuenI6RmdN0tu_y5HwCDIL8r0KPxpf2Ub1ePsZazmmVeaKQXDKVr2jyC9pYIwinSe899zDWs2j7U0rAlxhSJjg8p8kZw-ips574Aa7VW9I9ULDdxBRy5g9zF-eACwLa5jPJ5T-HF-Y7YUDel2tq8qmracC6s-5joHUrM9vrMaqtNOExFmrxLOjPPiRnSrL1GPptqJ0yb7Fe2dug.y3eBV6lxlpKfkS8S5Z8MUafJ7edoOSrmeitg5Rii1ig&dib_tag=se&keywords=the+customer+first+business&qid=1769299722&s=books&sprefix=the+customer+first+busines%2Caps%2C141&sr=1-1"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-book"
                  className="inline-flex items-center justify-center bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 shadow-lg"
                >
                  Get The Book <ArrowRight className="ml-2" size={20} />
                </a>
                <a
                  href="https://youtube.com/@TheServantCEO"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-coaching"
                  className="inline-flex items-center justify-center border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-full px-8 py-4 text-lg font-medium transition-all text-center"
                >
                  Visit The Servant CEO<br />YouTube Channel
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="glass-card rounded-2xl p-8 shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/nr9qg4em_Book%20Cover.jpg"
                  alt="The Customer First Business Book by Will Owen"
                  className="w-full rounded-lg shadow-xl"
                  data-testid="hero-book-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="services-title">
              How Can I Help You?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether through the insights in my book, business coaching videos on my YouTube Channel or speaking at your next event, I'm here to help you find success that lasts.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="service-card-book"
              className="glass-card rounded-2xl p-8 hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-6">
                <BookOpen className="text-accent" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4">The Book</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                A comprehensive guide to implementing customer-first principles and servant leadership in your organization.
              </p>
              <Link
                to="/book"
                data-testid="service-link-book"
                className="inline-flex items-center text-accent hover:underline font-medium"
              >
                Learn More <ArrowRight className="ml-2" size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              data-testid="service-card-coaching"
              className="glass-card rounded-2xl p-8 hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-6">
                <Play className="text-accent" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Business Coaching Through Our YouTube Channel</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Subscribe to our YouTube Channel to learn first hand from Will Owen, The Servant CEO.
              </p>
              <Link
                to="/coaching"
                data-testid="service-link-coaching"
                className="inline-flex items-center text-accent hover:underline font-medium"
              >
                Learn More <ArrowRight className="ml-2" size={16} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              data-testid="service-card-speaking"
              className="glass-card rounded-2xl p-8 hover:-translate-y-2 hover:shadow-xl transition-all duration-300"
            >
              <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mb-6">
                <Mic className="text-accent" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Speaking Engagements</h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Keynote speeches and workshops that inspire teams and leaders to embrace servant leadership principles.
              </p>
              <Link
                to="/speaking"
                data-testid="service-link-speaking"
                className="inline-flex items-center text-accent hover:underline font-medium"
              >
                Learn More <ArrowRight className="ml-2" size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_growth-guide-21/artifacts/7fzjpgkl_AdobeStock_753198316.jpeg"
                alt="Business professionals shaking hands"
                className="rounded-2xl shadow-2xl w-full h-full object-cover"
                data-testid="benefits-image"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col justify-between"
            >
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-12" data-testid="benefits-title">
                  Why Building A Customer First Business Matters
                </h2>
                <div className="space-y-8">
                  {[
                    { title: 'Accelerated Trust:', text: 'By focusing on a specific niche rather than a generic crowd, you establish immediate authority and transform skeptical prospects into loyal believers.' },
                    { title: 'Guaranteed Demand:', text: 'Prioritizing their actual problems ensures you are building a "must-have" solution that people are already looking for, rather than trying to manufacture a need for a product.' },
                    { title: 'Reduced Marketing Friction:', text: 'When you solve deep pain points, your customers become your most effective sales force, driving growth through organic word-of-mouth and high-value referrals.' },
                    { title: 'Strategic Scalability:', text: 'A customer-first mindset provides a clear roadmap for growth; instead of guessing where to expand, you simply follow the evolving needs of your core community.' },
                    { title: 'Resilience Against Competition:', text: 'While competitors fight over price and features, a customer-centric business wins on relationship and results, making your brand nearly impossible to displace.' }
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-start space-x-4"
                      data-testid={`benefit-item-${index}`}
                    >
                      <CheckCircle className="text-accent flex-shrink-0 mt-1" size={24} />
                      <p className="text-lg leading-relaxed">
                        <span className="font-bold">{benefit.title}</span> {benefit.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-secondary/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" data-testid="testimonials-title">
                What Leaders Are Saying
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  data-testid={`testimonial-card-${index}`}
                  className="glass-card rounded-2xl p-8"
                >
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-accent fill-accent" size={20} />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {testimonial.position}, {testimonial.company}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-card rounded-2xl p-12 text-center"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-testid="newsletter-title">
              Stay Connected
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Get insights, updates, and exclusive content delivered to your inbox.
            </p>
            <NewsletterSignup />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;